import { useCallback, useEffect, useRef } from 'react';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE, getAnalyticsEventsFromTransaction } from '@atlaskit/editor-common/analytics';
import { getDocStructure } from '@atlaskit/editor-common/core-utils';
import { startMeasure, stopMeasure } from '@atlaskit/editor-common/performance-measures';
import { findChangedNodesFromTransaction } from '../../utils/findChangedNodesFromTransaction';
import { freezeUnsafeTransactionProperties } from '../../utils/performance/safer-transactions';
import { EVENT_NAME_ON_CHANGE } from '../../utils/performance/track-transactions';
import { validateNodes, validNode } from '../../utils/validateNodes';
export var useDispatchTransaction = function useDispatchTransaction(_ref) {
  var onChange = _ref.onChange,
    dispatchAnalyticsEvent = _ref.dispatchAnalyticsEvent,
    onEditorViewUpdated = _ref.onEditorViewUpdated;
  // We need to have a ref to the latest `onChange` since the `dispatchTransaction` gets captured
  var onChangeRef = useRef(onChange);
  useEffect(function () {
    onChangeRef.current = onChange;
  }, [onChange]);
  var dispatchTransaction = useCallback(function (view, unsafeTransaction) {
    if (!view) {
      return;
    }
    var nodes = findChangedNodesFromTransaction(unsafeTransaction);
    var changedNodesValid = validateNodes(nodes);
    var transaction = new Proxy(unsafeTransaction, freezeUnsafeTransactionProperties({
      dispatchAnalyticsEvent: dispatchAnalyticsEvent,
      pluginKey: 'unknown-reacteditorview'
    }));
    if (changedNodesValid) {
      var oldEditorState = view.state;

      // go ahead and update the state now we know the transaction is good
      var _view$state$applyTran = view.state.applyTransaction(transaction),
        newEditorState = _view$state$applyTran.state,
        transactions = _view$state$applyTran.transactions;
      if (newEditorState === oldEditorState) {
        return;
      }
      view.updateState(newEditorState);
      onEditorViewUpdated({
        originalTransaction: transaction,
        transactions: transactions,
        oldEditorState: oldEditorState,
        newEditorState: newEditorState
      });
      if (onChangeRef.current && transaction.docChanged) {
        var source = transaction.getMeta('isRemote') ? 'remote' : 'local';
        startMeasure(EVENT_NAME_ON_CHANGE);
        onChangeRef.current(view, {
          source: source
        });
        stopMeasure(EVENT_NAME_ON_CHANGE);
      }
    } else {
      var invalidNodes = nodes.filter(function (node) {
        return !validNode(node);
      }).map(function (node) {
        return getDocStructure(node, {
          compact: true
        });
      });
      dispatchAnalyticsEvent({
        action: ACTION.DISPATCHED_INVALID_TRANSACTION,
        actionSubject: ACTION_SUBJECT.EDITOR,
        eventType: EVENT_TYPE.OPERATIONAL,
        attributes: {
          analyticsEventPayloads: getAnalyticsEventsFromTransaction(transaction),
          invalidNodes: invalidNodes
        }
      });
    }
  }, [dispatchAnalyticsEvent, onEditorViewUpdated]);
  return dispatchTransaction;
};